// iter-35a — Account Termination Policy.
//
// Public-facing legal page linked from the Account Settings DangerZone, the
// FAQ tab, and the site footer. Versioned via the LAST_UPDATED constant — bump
// it whenever the policy changes so users (and audit) can see the change date.
//
// This is intentionally a STATIC page (no fetch) so the policy is always
// visible even when backend is down.

import { Link } from "react-router-dom";
import { Sparkle, ArrowLeft, ShieldCheck, Warning, Clock, UserMinus, Lock } from "@phosphor-icons/react";
import { SUPPORT_EMAIL } from "@/lib/contact";

const LAST_UPDATED = "May 3, 2026";

export default function TerminationPolicy() {
  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="termination-policy-page">
      <header className="border-b hairline bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1C1B1A] text-[#FDFCF8] flex items-center justify-center">
              <Sparkle size={18} weight="duotone" />
            </div>
            <div>
              <div className="font-serif-display text-xl leading-none">My Silo</div>
              <div className="text-[10px] text-[#6B6A68] tracking-[0.25em] uppercase mt-1">Policy</div>
            </div>
          </Link>
          <Link to="/dashboard" className="text-sm text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1.5" data-testid="policy-back">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <main className="siloart-copy max-w-3xl mx-auto px-6 py-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C16E5A] mb-2 flex items-center gap-1.5">
          <ShieldCheck size={12} weight="duotone" /> Account Termination Policy
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1C1B1A] leading-tight">
          When and how accounts <em className="text-[#4A5D4E]">end</em>.
        </h1>
        <p className="text-sm text-[#6B6A68] mt-4 max-w-2xl">
          This page explains every reason a My Silo account can be terminated — voluntary or involuntary — and what happens to the user's data, silos, subscriptions, and referral relationships afterward. Last updated <strong>{LAST_UPDATED}</strong>.
        </p>

        {/* Summary card */}
        <div className="mt-10 bg-white border hairline rounded-xl p-6" data-testid="policy-summary">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A] flex items-center gap-2">
            <Clock size={20} weight="duotone" className="text-[#4A5D4E]" /> The 30-day rule
          </h2>
          <p className="text-sm text-[#1C1B1A] mt-3 leading-relaxed">
            When you initiate account deletion, your account enters a <strong>30-day grace window</strong>. During this window:
          </p>
          <ul className="mt-3 space-y-2 text-sm text-[#1C1B1A]">
            <li className="flex gap-2"><span className="text-[#4A5D4E]">•</span> Your silos go private — no one can access them.</li>
            <li className="flex gap-2"><span className="text-[#4A5D4E]">•</span> Your platform subscription is cancelled (no further billing).</li>
            <li className="flex gap-2"><span className="text-[#4A5D4E]">•</span> You can sign back in any time to reactivate — full restoration.</li>
            <li className="flex gap-2"><span className="text-[#4A5D4E]">•</span> After 30 days: hard delete. No recovery possible.</li>
          </ul>
        </div>

        {/* Voluntary */}
        <section className="mt-10" data-testid="policy-voluntary">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A] flex items-center gap-2">
            <UserMinus size={20} weight="duotone" className="text-[#4A5D4E]" /> Voluntary termination
          </h2>
          <p className="text-sm text-[#6B6A68] mt-2 max-w-2xl">
            Reasons you might end your own account.
          </p>
          <div className="mt-4 space-y-4">
            <PolicyItem
              n={1}
              title="Self-deletion"
              body="You request account deletion via Account Settings → Danger Zone → Delete Account. The 30-day grace rule applies."
            />
            <PolicyItem
              n={2}
              title="Cancellation + 180-day dormancy"
              body="If you cancel your platform subscription and don't sign in for 180 consecutive days afterward, the system flags your account as abandoned and triggers the same 30-day deletion window. You'll receive an email at day 150 warning you. Sign in any time during the warning or grace period to keep your account."
            />
            <PolicyItem
              n={3}
              title="Account merge (future)"
              body="If you have multiple accounts you'd like merged into one, contact platform support. The merge target absorbs all silos, subscriptions, and referral history; the source account is hard-deleted upon merge confirmation."
            />
          </div>
        </section>

        {/* Involuntary */}
        <section className="mt-10" data-testid="policy-involuntary">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A] flex items-center gap-2">
            <Warning size={20} weight="duotone" className="text-[#C16E5A]" /> Involuntary termination
          </h2>
          <p className="text-sm text-[#6B6A68] mt-2 max-w-2xl">
            We may terminate accounts that violate the rules below. We always
            attempt to warn first when feasible; severe violations result in
            immediate termination without grace.
          </p>
          <div className="mt-4 space-y-4">
            <PolicyItem n={4} title="Abuse or harassment" body="Targeting other users, creators, or fans with threats, doxxing, persistent insults, or sexual harassment. Zero-tolerance — immediate ban." />
            <PolicyItem n={5} title="Spam or scraping" body="Mass-signups via automation, scraping public silo content for AI training datasets without permission, or sending unsolicited bulk messages through the Communications system." />
            <PolicyItem n={6} title="Payment fraud" body="Stolen credit card use, repeated chargebacks, manufactured signups to game referral credits (per-card cap violations escalate from warning to termination)." />
            <PolicyItem n={7} title="Self-referral gaming" body="Creating fake fan accounts to refer yourself, or coordinating with friends/family to bypass the per-card-cap protections. The platform-fee revenue from a single household is capped at 10 referred Creator signups." />
            <PolicyItem n={8} title="Illegal content" body="Uploading material that violates child safety laws, copyright (when the rights-holder takes formal action), or applicable law in your jurisdiction." />
            <PolicyItem n={9} title="Hate speech & bigotry" body="Content targeting protected groups, calls for violence, or repeated dog-whistling beyond reasonable interpretation. Subjective — handled case-by-case by platform admin with full transparency on the reason." />
            <PolicyItem n={10} title="Impersonation" body="Operating a silo under another person's name, likeness, or trademark without authorization. Verified rights-holders may file a takedown via support; repeat offenses are permanent bans." />
            <PolicyItem n={11} title="KYC, tax, or compliance failure" body="When you request a payout via Stripe Connect, KYC verification is required. Failure to complete KYC within 90 days of first payout request, or operating from a sanctioned country, results in restriction of payouts and eventual deletion." />
            <PolicyItem n={12} title="Inactivity escheatment" body="24+ months of zero login while holding any monetary balance triggers our escheatment protocol. Funds revert to the platform per applicable abandoned-property law. Account is hard-deleted." />
            <PolicyItem n={13} title="Confirmed deceased" body="When the platform receives credible verification of an account holder's passing, we suspend the account immediately and work with named executors or next of kin to either close it or transfer ownership where applicable." />
            <PolicyItem n={14} title="Repeated payment-provider chargebacks" body="If Stripe imposes restrictions or termination on your payment account due to chargeback rate, we mirror that decision to keep the platform in good standing." />
          </div>
        </section>

        {/* What happens to your stuff */}
        <section className="mt-10" data-testid="policy-aftermath">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A] flex items-center gap-2">
            <Lock size={20} weight="duotone" className="text-[#4A5D4E]" /> What happens to your data and relationships
          </h2>
          <div className="mt-4 bg-white border hairline rounded-xl p-6 text-sm text-[#1C1B1A] space-y-3 leading-relaxed">
            <p><strong>Silos & documents:</strong> Permanently deleted from the database and ChromaDB at the moment of hard-delete. Public silo URLs return 404.</p>
            <p><strong>Fan subscriptions to your silos:</strong> Cancelled. Stripe stops billing fans automatically. Past payments stay with you (no refunds for past months).</p>
            <p><strong>Your subscriptions to other creators' silos:</strong> Cancelled. Stripe stops billing.</p>
            <p><strong>Bookmarks, tips given, conversations:</strong> Deleted with the account.</p>
            <p><strong>Referral relationships:</strong> Audit rows are preserved with an <code>orphaned_at</code> marker. Referral credits already <em>awarded</em> remain in the credit balances of unaffected creators. Pending or settling referrals attached to the terminated user are marked orphaned and do not pay out — they may be reattributed by future engagement-attribution sweeps (see iter-37 in our public roadmap).</p>
            <p><strong>Legal/financial holds:</strong> If you have an outstanding balance, payout, or chargeback dispute, hard-delete is paused until those resolve.</p>
          </div>
        </section>

        {/* Disputes */}
        <section className="mt-10" data-testid="policy-disputes">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Disputing a termination</h2>
          <p className="text-sm text-[#1C1B1A] mt-3 max-w-2xl leading-relaxed">
            If your account was terminated involuntarily and you believe it was an error, email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#4A5D4E] underline">{SUPPORT_EMAIL}</a> within 30 days with your account email and a description of the situation. We respond within 5 business days. Decisions on hate-speech, harassment, and illegal-content terminations are reviewed by at least two platform admins to keep the bar high.
          </p>
        </section>

        <div className="mt-12 pt-6 border-t hairline text-xs text-[#6B6A68] flex items-center justify-between">
          <span>Last updated: {LAST_UPDATED}</span>
          <Link to="/dashboard" className="text-[#4A5D4E] hover:text-[#1C1B1A]">Back to dashboard →</Link>
        </div>
      </main>
    </div>
  );
}

function PolicyItem({ n, title, body }) {
  return (
    <div className="bg-white border hairline rounded-md p-4" data-testid={`policy-item-${n}`}>
      <div className="flex items-start gap-3">
        <span className="font-serif-display text-2xl text-[#C16E5A] tabular-nums leading-none">{n}.</span>
        <div>
          <h3 className="text-base font-medium text-[#1C1B1A]">{title}</h3>
          <p className="text-sm text-[#6B6A68] mt-1 leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  );
}
